import { useState } from 'react';
import { ZmianaKlucza } from '@/components/ZmianaKlucza';
import { checkCardNumber, kartaDoZmianyNumeru, zmienNumerKarty } from '@/lib/employees-api';
import { useI18n } from '@/lib/i18n';

/**
 * ZMIANA NUMERU KARTY — odmiana wspólnego okna zmiany klucza (`ZmianaKlucza`).
 *
 * ⚠️ JEDYNA Z CZTERECH ODMIAN Z PTASZKIEM HISTORII, DOMYŚLNIE ODZNACZONYM. SPS przy zmianie
 * numeru NIE rusza rejestru wydań, więc pobrania odczepiają się od człowieka; zaznaczenie robi
 * więc COŚ WIĘCEJ niż SPS — daje ciągłość raportu, ale rozjeżdża nas z kopiami bazy zrobionymi
 * wcześniej po stronie klienta. Musi być świadomym wyborem.
 *
 * Przy kodzie grupy takiego ptaszka nie ma i nie będzie: `TransactionLog` trzyma grupy zamrożone
 * w DZIESIĘCIU kolumnach (`USERGROUP01..10`), a rejestr jest tabelą zakazaną — jedyny wyłom
 * dotyczy jednego wiersza wskazanego przez `TLID`.
 */
export function ZmianaNumeruKarty({
  employeeId,
  islandId,
  obecnyNumer,
  nazwisko,
  onClose,
  onSaved,
}: {
  employeeId: string;
  islandId: string;
  obecnyNumer: string;
  nazwisko: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  /**
   * ⚠️ WŁĄCZONY DOMYŚLNIE (2026-08-18) — tak samo jak przez API. Wymiana karty ma poprawiać numer
   * także wstecz, w rejestrze wydań: sam SPS tego nie robi, więc bez tego wydania zostają pod starym
   * numerem i tracą właściciela (zmierzone u klienta: 138 sztuk). Operator może odznaczyć.
   */
  const [zmienHistorie, setZmienHistorie] = useState(true);

  return (
    <ZmianaKlucza
      tytul={t('emp.cardChangeTitle')}
      podpis={`${nazwisko} · ${obecnyNumer}`}
      wstep={t('emp.cardChangeIntro')}
      ostrzezenie={t('emp.cardChangeWarn')}
      etykietaPola={t('emp.cardChangeNew')}
      obecny={obecnyNumer}
      /** `Users.USERNUMBER` — 32 znaki. */
      limit={32}
      sprawdz={async (v) => {
        const r = kartaDoZmianyNumeru(await checkCardNumber(islandId, v));
        return r.usunieta
          ? { ...r, powod: t('emp.cardChangeRemovedHolder').replace('{name}', r.takenBy?.name ?? '?') }
          : r;
      }}
      dodatkowy={{
        etykieta: t('emp.cardChangeHistory'),
        podpowiedz: t('emp.cardChangeHistoryHint'),
        wartosc: zmienHistorie,
        ustaw: setZmienHistorie,
      }}
      zapisz={async (nowy) => {
        const r = await zmienNumerKarty(employeeId, { groupId: islandId, nowyNumer: nowy, zmienHistorie });
        return t('emp.cardChanged')
          .replace('{stary}', r.stary)
          .replace('{nowy}', r.nowy)
          .replace('{n}', String(r.wydan));
      }}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}
